import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Thermometer,
  House,
  Drop,
  Flame,
  Leaf,
  CheckCircle,
  ArrowRight,
  Phone,
  Wrench,
} from "@phosphor-icons/react";
import { COMPANY, IMAGES } from "../lib/constants";
import CallbackForm from "../components/CallbackForm";

const services = [
  {
    id: "pompe-a-chaleur",
    icon: Thermometer,
    title: "Pompe à chaleur air/eau",
    subtitle: "Chauffage performant et moderne",
    desc: "La pompe à chaleur air/eau capte l'énergie présente dans l'air extérieur pour chauffer l'eau de votre circuit de chauffage (radiateurs, plancher chauffant) et votre eau chaude sanitaire. Une solution plébiscitée pour sa performance et sa simplicité d'usage.",
    bullets: [
      "Adaptée aux maisons individuelles",
      "Compatible avec la plupart des circuits de chauffage",
      "Réduction significative possible des consommations",
      "Maintenance simple et durable",
    ],
    image: IMAGES.heatpump,
  },
  {
    id: "renovation-energetique",
    icon: Leaf,
    title: "Rénovation énergétique",
    subtitle: "Un logement plus performant, plus confortable",
    desc: "La rénovation énergétique regroupe un ensemble de travaux visant à améliorer la performance globale de votre logement : isolation, changement de chauffage, régulation. Elle permet un meilleur confort thermique et une baisse potentielle des consommations.",
    bullets: [
      "Audit énergétique global",
      "Solutions priorisées selon l'impact",
      "Aides potentiellement mobilisables",
      "Artisans qualifiés pour les travaux",
    ],
    image: IMAGES.renovation,
  },
  {
    id: "remplacement-chauffage",
    icon: Flame,
    title: "Remplacement de chauffage",
    subtitle: "Fioul, gaz : place à des solutions plus efficientes",
    desc: "Le remplacement d'une ancienne chaudière fioul ou gaz par une solution plus performante (pompe à chaleur, ballon thermodynamique) permet de réduire durablement vos consommations tout en améliorant le confort de votre logement.",
    bullets: [
      "Étude de compatibilité avec l'existant",
      "Dimensionnement précis",
      "Installation par un professionnel qualifié",
      "Accompagnement administratif",
    ],
    image: IMAGES.house,
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="eco-gradient py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700 mb-3">
            Nos solutions
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
            Des solutions énergétiques
            <br className="hidden md:block" />
            <span className="bg-gradient-to-r from-blue-700 to-emerald-600 bg-clip-text text-transparent">
              adaptées à votre logement
            </span>
          </h1>
          <p className="text-slate-600 mt-5 text-base md:text-lg max-w-2xl leading-relaxed">
            Pompe à chaleur, rénovation énergétique, remplacement de chauffage :
            nous vous accompagnons sur l'ensemble de votre projet, avec clarté
            et sans engagement.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {services.map((s, i) => {
            const Icon = s.icon;
            const imageFirst = i % 2 === 1;
            return (
              <motion.div
                key={s.id}
                id={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5 }}
                data-testid={`service-${s.id}`}
                className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
              >
                <div className={imageFirst ? "lg:order-2" : ""}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center mb-4">
                    <Icon size={22} weight="fill" color="#FFFFFF" />
                  </div>
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-2">
                    {s.subtitle}
                  </div>
                  <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                    {s.title}
                  </h2>
                  <p className="text-slate-600 leading-relaxed mb-6">{s.desc}</p>
                  <ul className="space-y-3 mb-7">
                    {s.bullets.map((b, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3"
                        data-testid={`service-${s.id}-bullet-${idx}`}
                      >
                        <CheckCircle
                          size={18}
                          weight="fill"
                          className="text-emerald-600 flex-shrink-0 mt-0.5"
                        />
                        <span className="text-sm text-slate-700">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/eligibilite"
                      data-testid={`service-${s.id}-eligibility-btn`}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold btn-primary-eco"
                    >
                      Vérifier mon éligibilité <ArrowRight size={16} weight="bold" />
                    </Link>
                    <a
                      href={COMPANY.phoneHref}
                      data-testid={`service-${s.id}-call-btn`}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold border-2 border-slate-200 text-slate-700 hover:bg-slate-50"
                    >
                      <Phone size={16} weight="fill" /> Appeler un conseiller
                    </a>
                  </div>
                </div>
                <div className={imageFirst ? "lg:order-1" : ""}>
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-[380px] lg:h-[460px] object-cover rounded-3xl shadow-[0_20px_60px_rgba(30,64,175,0.12)]"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-3">
              Un projet en tête ? Parlons-en.
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Laissez-nous vos coordonnées, un conseiller Hélios Solutions vous
              rappelle rapidement pour étudier votre projet. Étude personnalisée
              et sans engagement.
            </p>
            <div className="mt-5 flex items-center gap-2 text-sm text-slate-600">
              <Wrench size={18} className="text-blue-700" />
              Artisans qualifiés • Accompagnement complet
            </div>
          </div>
          <CallbackForm testIdPrefix="services-callback" />
        </div>
      </section>
    </div>
  );
}
